import {
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';
import {
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
  Droppable,
  DroppableProvided,
  DroppableStateSnapshot,
} from 'react-beautiful-dnd';
import HeadCell from '../../../../../components/muiLegacy/Tables/Shared/HeadCell';
import VoxelAdvancedTextSearch from '../../../../../components/voxel/Search/VoxelAdvancedTextSearch';
import VoxelCheckbox from '../../../../../components/voxel/Forms/VoxelCheckbox';
import { ContentKeywordGq } from '../../../../../graphql/projects/models/ContentKeywordGq';
import { ContentTaskGq } from '../../../../../graphql/projects/models/ContentTaskGq';
import { ProjectGq } from '../../../../../graphql/projects/models/ProjectGq';
import { TagGroupGq } from '../../../../../graphql/projects/models/TagGroupGq';
import SerpResultsDialog, {
  DisplaySerpResult,
} from '../../../../DataManagementPage/components/Dialogs/SerpResultsDialog';
import { useSelectUrl } from '../../Dialogs/ContentTaskDialog/useSelectUrl';
import ContentTaskUrlDialog from '../../Dialogs/ContentTaskUrlDialog/ContentTaskUrlDialog';
import ContentKeywordSpeedDial from './ContentKeywordSpeedDial';
import ContentKeywordTableRow from './ContentKeywordTableRow';
import HeadCellVolume from './HeadCellVolume';
import { useContentSelectBag } from './logic/useContentSelectBag';
import { useContentTaskTableState } from './logic/useContentTaskTableState';

export interface ContentKeywordTableProps {
  project: ProjectGq;
  contentTask: ContentTaskGq;
  clientUrl: string;
  reload: () => Promise<any>;
  localItems: ContentKeywordGq[];
  setLocalItems: Dispatch<SetStateAction<ContentKeywordGq[]>>;
  isAdding?: boolean;
  isDragginGlobal?: boolean;
  showTags?: TagGroupGq[];
}

const ContentKeywordTable: React.FC<ContentKeywordTableProps> = ({
  project,
  contentTask,
  reload,
  isAdding,
  isDragginGlobal,
  clientUrl,
  localItems,
  setLocalItems,
  showTags,
}) => {
  const [displaySerpResult, setDisplaySerpResult] = useState<DisplaySerpResult>({
    keywordId: undefined,
    rankNumber: undefined,
  });

  // Table filtering
  const [taskKeywordSearchTerm, setTaskKeywordSearchTerm] = useState('');
  const filteredKeywords = useMemo(
    () =>
      localItems
        .map((contentKeyword, index) => ({ contentKeyword, index }))
        .filter(
          (item) =>
            !taskKeywordSearchTerm?.length ||
            !!item.contentKeyword?.keyword
              ?.toLowerCase()
              .includes(taskKeywordSearchTerm.toLowerCase())
        ),
    [localItems, taskKeywordSearchTerm]
  );

  // Table state
  const tableState = useContentTaskTableState({
    invertSelection: false,
    contentKeywordIds: [],
    emptyIsAll: false,
  });

  // Use selectBag
  const selectBag = useContentSelectBag(tableState, localItems);
  const selectedIds = useMemo(
    () =>
      filteredKeywords
        .filter((fk) => {
          if (tableState.selectionState.invertSelection) {
            return !tableState.selectionState.contentKeywordIds[fk.contentKeyword.id];
          }
          return tableState.selectionState.contentKeywordIds[fk.contentKeyword.id];
        })
        .map((item) => item.contentKeyword.id),
    [filteredKeywords, tableState]
  );
  const selectCount = selectedIds.length;
  const selectActive = !!selectCount;

  // Select new url
  const selectUrlBag = useSelectUrl({
    contentTask,
    reload,
    clientUrl,
  });

  // Tags
  const [hideTags, setHideTags] = useState(false);

  return (
    <>
      <ContentTaskUrlDialog selectUrlBag={selectUrlBag} projectId={contentTask.projectId} />
      <div className="relative">
        <ContentKeywordSpeedDial
          selectedIds={selectedIds}
          clientUrl={clientUrl}
          setLocalItems={setLocalItems}
          localItems={localItems}
          selectedCount={selectCount}
          selectActive={!!selectActive}
          contentTask={contentTask}
          tableState={tableState}
          reload={reload}
        />
        <TableContainer className={isAdding ? 'h-[315px]' : 'h-[660px]'} component="div">
          <Table stickyHeader size={'small'} component="div">
            <TableHead component="div">
              <TableRow component="div">
                <TableCell padding={'none'} component="div">
                  Status
                </TableCell>
                <TableCell padding={'checkbox'} component="div">
                  <VoxelCheckbox
                    color="primary"
                    checked={tableState.selectionState.invertSelection}
                    onChange={() =>
                      tableState.setSelectionState((prevState) => ({
                        contentKeywordIds: [],
                        invertSelection: !prevState.invertSelection,
                        emptyIsAll: true,
                      }))
                    }
                  />
                </TableCell>
                <HeadCell
                  hasFilter={!!taskKeywordSearchTerm.length}
                  basicConfig={{ title: 'Keyword', hideColumnDisabled: true }}
                  basicState={{ showColumn: true, hideColumn: () => {} }}
                  align="left"
                >
                  <div className={'px-2 py-3'}>
                    <VoxelAdvancedTextSearch
                      size="sm"
                      onChange={(event) => setTaskKeywordSearchTerm(event.currentTarget.value)}
                      value={taskKeywordSearchTerm}
                      placeholder="Keyword filter"
                    />
                  </div>
                </HeadCell>
                {project.hasEnglishTranslation && <TableCell component="div">English</TableCell>}
                <HeadCellVolume
                  taskId={contentTask.id}
                  taskKeywords={localItems}
                  reload={reload}
                  projectId={project.id}
                />
                {!!showTags?.length && (
                  <TableCell component="div" align={'left'}>
                    <Grid container alignItems="center" spacing={1} wrap="nowrap">
                      <Grid item>Tags</Grid>
                      <Grid item>
                        <IconButton
                          onClick={() => setHideTags((prev) => !prev)}
                          size="small"
                          color="default"
                        >
                          {hideTags ? (
                            <VisibilityOff fontSize="small" color="inherit" />
                          ) : (
                            <Visibility fontSize="small" color="inherit" />
                          )}
                        </IconButton>
                      </Grid>
                    </Grid>
                  </TableCell>
                )}
                <TableCell component="div" align={'right'}>
                  Title
                </TableCell>
                <TableCell component="div" align={'right'}>
                  H1
                </TableCell>
                <TableCell component="div" align={'right'}>
                  Content
                </TableCell>
                <TableCell component="div" align={'right'}>
                  URL rank
                </TableCell>
                <TableCell align={'left'} colSpan={2}>
                  Other best rank
                </TableCell>
              </TableRow>
            </TableHead>
            <Droppable droppableId={'mainTable'} direction="vertical">
              {(
                droppableProvided: DroppableProvided,
                droppableSnapshot: DroppableStateSnapshot
              ) => (
                <TableBody
                  component="div"
                  ref={droppableProvided.innerRef}
                  {...droppableProvided.droppableProps}
                  className={droppableSnapshot.isDraggingOver ? 'bg-voxel-gray-50' : ''}
                >
                  {filteredKeywords.map((item) => {
                    const contentKeyword = item.contentKeyword;
                    const index = item.index;
                    return (
                      <Draggable
                        key={contentKeyword.id}
                        draggableId={'main-table-keyword-' + contentKeyword.keywordId.toString()}
                        index={index}
                      >
                        {(
                          draggableProvided: DraggableProvided,
                          draggableSnapshot: DraggableStateSnapshot
                        ) => {
                          return (
                            <ContentKeywordTableRow
                              showTags={showTags}
                              project={project}
                              selectNewUrl={selectUrlBag.setEvaluatedUrl}
                              key={contentKeyword.id}
                              clientUrl={clientUrl}
                              contentTaskUrl={contentTask.url}
                              contentKeyword={contentKeyword}
                              provided={draggableProvided}
                              snapshot={draggableSnapshot}
                              isDragginGlobal={isDragginGlobal}
                              selectionState={tableState.selectionState}
                              isSelected={selectBag.isIndexSelected(index)}
                              onMouseOver={selectBag.onMouseOver}
                              onMouseDown={selectBag.onMouseDown}
                              onMouseUp={selectBag.deactivateDragMode}
                              index={index}
                              setDisplaySerpResult={setDisplaySerpResult}
                              hideTags={hideTags}
                            />
                          );
                        }}
                      </Draggable>
                    );
                  })}
                  {droppableProvided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </Table>
          <Droppable droppableId={'whiteEmptySpace'} direction="vertical">
            {(droppableProvided: DroppableProvided, droppableSnapshot: DroppableStateSnapshot) => (
              <div
                className={'h-[100px]'}
                ref={droppableProvided.innerRef}
                {...droppableProvided.droppableProps}
              >
                {droppableProvided.placeholder}
              </div>
            )}
          </Droppable>
        </TableContainer>
      </div>
      {!!displaySerpResult.keywordId && (
        <SerpResultsDialog
          project={project}
          setDisplaySerpResult={setDisplaySerpResult}
          displaySerpResult={displaySerpResult}
        />
      )}
    </>
  );
};

export default ContentKeywordTable;
